/**
 * Login Dialog Component
 */
import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { getErrorMessage } from '../../utils/errorMessages';
import '../Dialogs/Dialog.css';
import './LoginDialog.css';

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginDialog: React.FC<LoginDialogProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuthStore();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(email, password);

      // Wait for localStorage to persist (check multiple times)
      let attempts = 0;
      while (attempts < 10) {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.state?.token) {
            console.log('[LoginDialog] Token persisted to localStorage');
            break;
          }
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      onClose();
      // Reload page to refresh all data with authentication
      window.location.reload();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Prihlásenie zlyhalo. Skúste to znova.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog login-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🔐 Prihlásenie</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vas@email.sk"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Heslo</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Zrušiť
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Prihlasovanie...' : 'Prihlásiť sa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
